import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaBuilding, FaTimes, FaChevronDown } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api'; // Adjust path if needed
import useColleges from '../../../utils/useColleges'; // Adjust path if needed
import './AddHead.css';

const AddHead = () => {
  const { colleges, loading: collegesLoading } = useColleges();
  
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', colleges: [] });
  
  // Dropdown State for Multi-Select
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [collegeSearchQuery, setCollegeSearchQuery] = useState('');
  
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // ── Load Heads ──────────────────────────────────────────────
  const loadHeads = async () => {
    setLoading(true);
    try {
      // NOTE: Ensure your backend endpoint matches. (e.g., /heads or /fees/heads)
      const { data } = await api.get('/expenses/heads');
      setHeads(data.data || data || []); 
    } catch (error) {
      console.error('Failed to load heads', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHeads(); }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false);
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Multi-Select Logic ──────────────────────────────────────────
  const filteredMultiColleges = colleges.filter(college =>
    college.name.toLowerCase().includes(collegeSearchQuery.toLowerCase())
  );

  const toggleCollege = (collegeId) => {
    setForm(prev => ({
      ...prev,
      colleges: prev.colleges.includes(collegeId)
        ? prev.colleges.filter(id => id !== collegeId)
        : [...prev.colleges, collegeId]
    }));
  };

  const handleSelectAll = () => {
    if (form.colleges.length === colleges.length) {
      setForm(prev => ({ ...prev, colleges: [] }));
    } else {
      setForm(prev => ({ ...prev, colleges: colleges.map(c => c._id) }));
    }
  };

  const removeCollege = (e, collegeId) => {
    e.stopPropagation(); 
    setForm(prev => ({ ...prev, colleges: prev.colleges.filter(id => id !== collegeId) }));
  };

  // ── Form Actions ────────────────────────────────────────────────
  const resetForm = () => {
    setForm({ name: '', colleges: [] });
    setEditId(null);
    setShowAddForm(false);
    setCollegeSearchQuery('');
    setDropdownOpen(false);
  };

  const handleSubmit = async () => {
    if (form.colleges.length === 0) return Swal.fire('Validation Error', 'Please select at least one College.', 'warning');
    if (!form.name.trim()) return Swal.fire('Validation Error', 'Please enter a Head / Party Name.', 'warning');

    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.put(`/expenses/heads/${editId}`, { name: form.name.trim(), colleges: form.colleges });
        setHeads(prev => prev.map(h => h._id === editId ? (data.data || data) : h));
        Swal.fire('Updated!', 'Head has been updated.', 'success');
      } else {
        const { data } = await api.post('/expenses/heads', { name: form.name.trim(), colleges: form.colleges });
        setHeads(prev => [data.data || data, ...prev]);
        Swal.fire('Added!', 'New Head has been added.', 'success');
      }
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save head.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (head) => {
    setEditId(head._id);
    setForm({
      name: head.name || '',
      colleges: head.colleges?.map(c => c._id || c) || [] 
    });
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (head) => {
    Swal.fire({
      title: `Delete "${head.name}"?`, text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e74c3c', confirmButtonText: 'Yes, delete it',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await api.delete(`/expenses/heads/${head._id}`);
        setHeads(prev => prev.filter(h => h._id !== head._id));
        Swal.fire('Deleted!', 'Head deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  // ── Filters & Grouping ──────────────────────────────────────────
  const displayedHeads = useMemo(() => {
    let res = heads;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      res = res.filter(h => h.name.toLowerCase().includes(q));
    }
    return res;
  }, [heads, searchQuery]);

  // Determine which colleges to render tables for
  const collegesToDisplay = useMemo(() => {
    if (filterCollege) return colleges.filter(c => c._id === filterCollege);
    // Fallback if empty, at least show a default structure
    return colleges.length > 0 ? colleges : [{ _id: 'default', name: 'Loading Colleges...' }];
  }, [colleges, filterCollege]);

  return (
    <div className="add-head-container">
      <h2 className="add-head-title">Heads Management</h2>

      {/* ── Top Controls ── */}
      <div className="add-head-controls">
        <div className="add-head-search-wrapper" ref={searchRef}>
          <FaSearch className="add-head-search-icon" />
          <input
            type="text"
            className="add-head-search-input"
            placeholder="Search heads by name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="add-head-search-dropdown">
              <ul>
                {displayedHeads.length > 0 ? displayedHeads.map(h => (
                  <li key={h._id} onClick={() => { setSearchQuery(h.name); setShowSearchDropdown(false); }}>
                    {h.name}
                  </li>
                )) : (
                  <div className="add-head-search-empty">No heads found</div>
                )}
              </ul>
            </div>
          )}
        </div>

        <select 
          className="add-head-filter-select" 
          value={filterCollege} 
          onChange={e => setFilterCollege(e.target.value)}
        >
          <option value="">All Colleges</option>
          {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>

        <button className="add-head-btn add-head-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Head'}
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showAddForm && (
        <div className="add-head-inline-form">
          <h3 className="add-head-form-title">{editId ? '✏️ Edit Head' : 'Add New Head'}</h3>

          <div className="add-head-form-grid">
            
            {/* 1. Colleges Multi-Select */}
            <div className="add-head-form-group add-head-col-span-2">
              <label>Select College(s) <span className="add-head-req">*</span></label>
              <div className="add-head-select-container" ref={dropdownRef}>
                <div 
                  className={`add-head-select-box ${dropdownOpen ? 'add-head-active' : ''}`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {form.colleges.length === 0 ? (
                    <span className="add-head-placeholder">-- Select Colleges --</span>
                  ) : (
                    form.colleges.map(id => {
                      const college = colleges.find(c => c._id === id);
                      if (!college) return null;
                      return (
                        <span key={id} className="add-head-tag">
                          <FaBuilding style={{ fontSize: '0.75rem' }} />
                          {college.name}
                          <span className="add-head-tag-remove" onClick={(e) => removeCollege(e, id)}><FaTimes /></span>
                        </span>
                      );
                    })
                  )}
                  <FaChevronDown className="add-head-chevron" />
                </div>

                {dropdownOpen && (
                  <div className="add-head-dropdown-menu">
                    <div className="add-head-dropdown-search">
                      <FaSearch className="add-head-search-icon-sm" />
                      <input
                        type="text"
                        placeholder="Search colleges..."
                        value={collegeSearchQuery}
                        onChange={(e) => setCollegeSearchQuery(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="add-head-select-all">
                      <span>{form.colleges.length} Selected</span>
                      <button type="button" onClick={handleSelectAll}>
                        {form.colleges.length === colleges.length ? 'Clear All' : 'Select All'}
                      </button>
                    </div>
                    <ul className="add-head-options-list">
                      {filteredMultiColleges.length > 0 ? filteredMultiColleges.map((college) => (
                        <li key={college._id} className="add-head-option" onClick={() => toggleCollege(college._id)}>
                          <input type="checkbox" checked={form.colleges.includes(college._id)} readOnly />
                          <span>{college.name}</span>
                        </li>
                      )) : (
                        <li className="add-head-empty-state">No colleges found.</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* 2. Head Name */}
            <div className="add-head-form-group add-head-col-span-2">
              <label>Head Name / Party Name <span className="add-head-req">*</span></label>
              <input 
                type="text" 
                value={form.name} 
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
                placeholder="Enter Head / Party Name" 
              />
            </div>

          </div>

          <div className="add-head-form-buttons">
            <button className="add-head-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-head-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Head' : 'Save Head'}
            </button>
          </div>
        </div>
      )}

      {/* ── Grouped Tables by College ── */}
      <div className="add-head-grouped-container">
        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading data...</div>}
        
        {!loading && collegesToDisplay.map(college => {
          // Find heads that belong to this particular college
          const collegeHeads = displayedHeads.filter(h => 
            h.colleges && h.colleges.some(c => (c._id || c) === college._id)
          );

          return (
            <div key={college._id} className="add-head-table-group">
              <h3 className="add-head-group-header">🏫 {college.name}</h3>
              <div className="add-head-table-wrapper">
                <table className="add-head-table">
                  <thead>
                    <tr>
                      <th width="80px">S.No</th>
                      <th className="add-head-align-left">Head / Party Name</th>
                      <th width="120px">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collegeHeads.length === 0 ? (
                      <tr>
                        <td colSpan="3" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                          No heads available for this college.
                        </td>
                      </tr>
                    ) : (
                      collegeHeads.map((head, i) => (
                        <tr key={head._id} className="add-head-main-row">
                          <td>{i + 1}</td>
                          <td className="add-head-align-left" style={{ fontWeight: 600, color: '#1e293b' }}>
                            {head.name}
                          </td>
                          <td>
                            <div className="add-head-action-btns">
                              <button className="add-head-btn-edit" onClick={() => handleEdit(head)} title="Edit"><FaEdit /></button>
                              <button className="add-head-btn-delete" onClick={() => handleDelete(head)} title="Delete"><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      
    </div>
  );
};

export default AddHead;